import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "@phosphor-icons/react";
import client, { API } from "@/lib/api";
import Stepper from "@/components/Stepper";

import { STEPS, readAsDataURL, uid } from "./inspection/constants";
import LandlordStep from "./inspection/LandlordStep";
import PropertyStep from "./inspection/PropertyStep";
import JobStep from "./inspection/JobStep";
import PhotosStep from "./inspection/PhotosStep";
import ChecklistStep from "./inspection/ChecklistStep";
import TenantStep from "./inspection/TenantStep";
import ReportStep from "./inspection/ReportStep";

export default function InspectionWizard() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [landlords, setLandlords] = useState([]);
  const [properties, setProperties] = useState([]);
  const [landlordId, setLandlordId] = useState("");
  const [propertyId, setPropertyId] = useState("");

  const [landlordForm, setLandlordForm] = useState({
    name: "", company_name: "", phone: "", email: "", address: "", notes: "",
  });
  const [propertyForm, setPropertyForm] = useState({
    landlord_id: "", address: "", eircode: "", property_type: "Apartment",
    ber_rating: "", furnished: "Unfurnished", bedrooms: 1, bathrooms: 1,
    heating_type: "Gas", parking: "None", notes: "",
  });
  const [inspection, setInspection] = useState(null);

  const [jobForm, setJobForm] = useState({
    inspection_type: "Move-In",
    inspection_date: new Date().toISOString().slice(0, 10),
    inspector_name: "", status: "Scheduled",
    move_in_date: "", move_out_date: "",
  });

  // ---- data loading ------------------------------------------------------
  const loadAll = useCallback(async () => {
    const [l, p] = await Promise.all([client.get("/landlords"), client.get("/properties")]);
    setLandlords(l.data);
    setProperties(p.data);
    if (id) {
      const { data } = await client.get(`/inspections/${id}`);
      setInspection(data);
      setLandlordId(data.landlord_id);
      setPropertyId(data.property_id);
      setJobForm({
        inspection_type: data.inspection_type,
        inspection_date: data.inspection_date,
        inspector_name: data.inspector_name,
        status: data.status,
        move_in_date: data.move_in_date || "",
        move_out_date: data.move_out_date || "",
      });
      setStep(2);
    }
  }, [id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const selectedLandlord = useMemo(
    () => landlords.find((x) => x.id === landlordId),
    [landlords, landlordId],
  );
  const filteredProperties = useMemo(
    () => properties.filter((p) => p.landlord_id === landlordId),
    [properties, landlordId],
  );
  const selectedProperty = useMemo(
    () => properties.find((p) => p.id === propertyId),
    [properties, propertyId],
  );

  // ---- step actions ------------------------------------------------------
  const saveLandlord = async () => {
    if (landlordId) { setStep(1); return; }
    if (!landlordForm.name) { toast.error("Landlord name is required"); return; }
    try {
      const { data } = await client.post("/landlords", landlordForm);
      setLandlords([data, ...landlords]);
      setLandlordId(data.id);
      setPropertyForm({ ...propertyForm, landlord_id: data.id });
      toast.success("Landlord saved");
      setStep(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save landlord");
    }
  };

  const saveProperty = async () => {
    if (propertyId) { setStep(2); return; }
    if (!propertyForm.address) { toast.error("Property address is required"); return; }
    try {
      const body = {
        ...propertyForm,
        landlord_id: landlordId,
        bedrooms: Number(propertyForm.bedrooms),
        bathrooms: Number(propertyForm.bathrooms),
      };
      const { data } = await client.post("/properties", body);
      setProperties([data, ...properties]);
      setPropertyId(data.id);
      toast.success("Property saved");
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save property");
    }
  };

  const saveJob = async () => {
    if (!jobForm.inspector_name) { toast.error("Inspector name required"); return; }
    try {
      if (!inspection) {
        const { data } = await client.post("/inspections", { ...jobForm, property_id: propertyId });
        setInspection(data);
        toast.success("Inspection created");
      } else {
        const { data } = await client.patch(`/inspections/${inspection.id}`, jobForm);
        setInspection({ ...inspection, ...data });
        toast.success("Saved");
      }
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save inspection job");
    }
  };

  const patch = async (body) => {
    if (!inspection) return;
    const { data } = await client.patch(`/inspections/${inspection.id}`, body);
    setInspection({ ...inspection, ...data });
  };

  const addPhotos = async (files, category) => {
    const list = inspection?.photos ? [...inspection.photos] : [];
    for (const f of Array.from(files)) {
      const data_url = await readAsDataURL(f);
      list.push({ id: uid(), category, caption: "", data_url });
    }
    await patch({ photos: list });
    toast.success(`Added ${files.length} photo(s)`);
  };

  const removePhoto = async (pid) => {
    const list = (inspection.photos || []).filter((p) => p.id !== pid);
    await patch({ photos: list });
  };

  const addChecklistItem = async (room) => {
    const list = inspection?.checklist ? [...inspection.checklist] : [];
    list.push({ id: uid(), room, item: "New item", condition: "Good", notes: "", repair_required: false, photo: "" });
    await patch({ checklist: list });
  };

  const updateChecklistItem = async (cid, patchObj) => {
    const list = (inspection.checklist || []).map((c) => (c.id === cid ? { ...c, ...patchObj } : c));
    await patch({ checklist: list });
  };

  const removeChecklistItem = async (cid) => {
    const list = (inspection.checklist || []).filter((c) => c.id !== cid);
    await patch({ checklist: list });
  };

  const downloadPDF = async () => {
    const token = localStorage.getItem("trent_token");
    const r = await fetch(`${API}/inspections/${inspection.id}/report`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trent-report-${inspection.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Report downloaded");
  };

  return (
    <div data-testid="inspection-wizard">
      <button
        onClick={() => navigate("/admin-dashboard/inspections")}
        className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] hover:text-[#1E3A5F] mb-4 inline-flex items-center gap-1"
      >
        <ArrowLeft size={14} /> Back to inspections
      </button>
      <h1 className="trent-heading text-3xl md:text-4xl font-black tracking-tighter text-[#1E3A5F] mb-2">
        {id ? "Continue inspection" : "New inspection"}
      </h1>
      <p className="text-sm text-[#6B7280] mb-6">
        Seven steps from landlord to tribunal-ready PDF. You can jump between steps any time.
      </p>

      <Stepper steps={STEPS} current={step} onJump={setStep} />

      {step === 0 && (
        <LandlordStep
          landlords={landlords}
          landlordId={landlordId}
          setLandlordId={setLandlordId}
          landlordForm={landlordForm}
          setLandlordForm={setLandlordForm}
          propertyForm={propertyForm}
          setPropertyForm={setPropertyForm}
          onNext={saveLandlord}
        />
      )}

      {step === 1 && (
        <PropertyStep
          selectedLandlord={selectedLandlord}
          filteredProperties={filteredProperties}
          propertyId={propertyId}
          setPropertyId={setPropertyId}
          propertyForm={propertyForm}
          setPropertyForm={setPropertyForm}
          onPrev={() => setStep(0)}
          onNext={saveProperty}
        />
      )}

      {step === 2 && (
        <JobStep
          jobForm={jobForm}
          setJobForm={setJobForm}
          onPrev={() => setStep(1)}
          onNext={saveJob}
        />
      )}

      {step === 3 && inspection && (
        <PhotosStep
          inspection={inspection}
          addPhotos={addPhotos}
          removePhoto={removePhoto}
          patch={patch}
          onPrev={() => setStep(2)}
          onNext={() => setStep(4)}
        />
      )}

      {step === 4 && inspection && (
        <ChecklistStep
          inspection={inspection}
          property={selectedProperty}
          addChecklistItem={addChecklistItem}
          updateChecklistItem={updateChecklistItem}
          removeChecklistItem={removeChecklistItem}
          onPrev={() => setStep(3)}
          onNext={() => setStep(5)}
        />
      )}

      {step === 5 && inspection && (
        <TenantStep
          inspection={inspection}
          patch={patch}
          onNext={() => setStep(6)}
          onPrev={() => setStep(4)}
        />
      )}

      {step === 6 && inspection && (
        <ReportStep
          inspection={inspection}
          patch={patch}
          onPrev={() => setStep(5)}
          downloadPDF={downloadPDF}
        />
      )}
    </div>
  );
}
